import { useState, type ReactNode } from 'react';
import type { ActivitySubjectActionSummary, ActivitySubjectCard } from '@shared/types/activity-feed';
import type { LightViewAction } from '@shared/types/views';
import { dismissActivityRecommendation } from '@/lib/activity-feed';
import type { WorkGuidanceMotif } from '@/lib/work-guidance';
import { type ActivityActionTriageReason, getActivityActionTriageId } from './ActivityActionQueue';
import { ActivityForgeHandoffDialog, type ForgeHandoffSelection } from './ActivityForgeHandoffDialog';
import { ActivityInlineToolLauncher, isResolvableToolHandoff } from './ActivityInlineToolLauncher';
import type { LivingWorkHandlers } from './LivingWorkRow';
import { useInlineToolLaunchStatuses } from './useInlineToolLaunchStatuses';

type ToolHandoffAction = Extract<LightViewAction, { type: 'tool_handoff' }>;
type FlowHandoffAction = Extract<LightViewAction, { type: 'flow_handoff' }>;

interface InlineToolSelection {
  subject: ActivitySubjectCard;
  action: ActivitySubjectActionSummary;
  toolAction: ToolHandoffAction;
}

/**
 * The gestures of a task row, shared by Mon travail and the page of an account
 * (26/10/2026: one task, one row, the same gestures everywhere): set a task
 * aside with its motif, open its tool prefilled, hand a reusable need to the
 * Forge, follow a launch until it ends. `dialogs` renders the launcher and
 * the Forge handoff once per page.
 */
export const useLivingWorkGestures = ({
  onChanged,
  onTriaged,
  onError,
}: {
  /** A task moved (triaged, a launch ended): reload what the page shows. */
  onChanged: () => void | Promise<void>;
  /** After a triage, with the subject it touched (Mon travail refreshes its drawer). */
  onTriaged?: (subject: ActivitySubjectCard) => Promise<void> | void;
  onError: (message: string | null) => void;
}): { handlers: LivingWorkHandlers; dialogs: ReactNode } => {
  const [triagingActionId, setTriagingActionId] = useState<string | null>(null);
  const [inlineToolSelection, setInlineToolSelection] = useState<InlineToolSelection | null>(null);
  const [forgeHandoffSelection, setForgeHandoffSelection] = useState<ForgeHandoffSelection | null>(null);
  const { launchStatuses, registerLaunch } = useInlineToolLaunchStatuses({ onTerminal: () => { void onChanged(); } });

  const onTriageAction = async (
    subject: ActivitySubjectCard,
    action: ActivitySubjectActionSummary,
    triageReason: ActivityActionTriageReason,
    guidance?: { motif: WorkGuidanceMotif; note: string | null },
  ) => {
    setTriagingActionId(getActivityActionTriageId(subject, action));
    onError(null);
    try {
      await dismissActivityRecommendation({
        subjectId: subject.subjectId,
        subjectKey: subject.subjectKey,
        subjectKind: subject.subjectKind,
        subjectLabel: subject.subjectLabel,
        recommendationId: action.id,
        recommendationLabel: action.label,
        triageReason,
        ...(guidance ? { motif: guidance.motif, note: guidance.note } : {}),
        sourceRunId: action.sourceRunId,
        sourceActionId: action.sourceActionId,
        sourceActionIndex: action.sourceActionIndex,
      });
      // The row stays « in triage » until the page shows the new state.
      await Promise.all([onChanged(), onTriaged?.(subject)]);
    } catch (triageError) {
      onError(triageError instanceof Error ? triageError.message : 'Impossible de trier cette action.');
    } finally {
      setTriagingActionId(null);
    }
  };

  const onToolHandoff = (subject: ActivitySubjectCard, action: ActivitySubjectActionSummary, toolAction: ToolHandoffAction) => {
    if (isResolvableToolHandoff(toolAction)) {
      setInlineToolSelection({
        subject,
        action,
        toolAction: toolAction.origin
          ? { ...toolAction, origin: { ...toolAction.origin, subjectId: subject.subjectId, accountSubjectId: subject.accountSubjectId } }
          : toolAction,
      });
      return;
    }
    // Safety net for stale payloads carrying an invented toolKey: propose to
    // build the tool in the Forge instead of opening a dead-end launcher.
    setForgeHandoffSelection({
      actionLabel: action.label,
      summary: action.summary,
      origin: toolAction.origin ?? null,
      seedPrompt: [action.label, action.summary].filter((value): value is string => Boolean(value)).join(' — '),
    });
  };

  const onFlowHandoff = (_subject: ActivitySubjectCard, action: ActivitySubjectActionSummary, flowAction: FlowHandoffAction) => {
    setForgeHandoffSelection({
      actionLabel: action.label,
      summary: action.summary,
      seedPrompt: flowAction.seedPrompt,
      origin: flowAction.origin ?? null,
    });
  };

  const dialogs = (
    <>
      <ActivityInlineToolLauncher
        action={inlineToolSelection?.toolAction ?? null}
        onClose={() => setInlineToolSelection(null)}
        onLaunched={(result) => {
          if (!inlineToolSelection) return;
          registerLaunch(getActivityActionTriageId(inlineToolSelection.subject, inlineToolSelection.action), result);
        }}
      />
      <ActivityForgeHandoffDialog selection={forgeHandoffSelection} onClose={() => setForgeHandoffSelection(null)} />
    </>
  );

  return {
    handlers: {
      onTriageAction: (subject, action, reason, guidance) => { void onTriageAction(subject, action, reason, guidance); },
      onToolHandoff,
      onFlowHandoff,
      triagingActionId,
      launchStatuses,
    },
    dialogs,
  };
};
